/**
 * Export COLUMN CONTRACT — the exact 50 columns of the platform's campaign
 * export template (source: "export campaign template csv.csv" / ".xlsx",
 * verified byte-identical headers). NOTHING may be added or removed: this is
 * what clients import downstream.
 * `col` = the header written to the file. `get` maps a raw leads_cards lead
 * row to the cell value; missing source data yields '' (never fabricated).
 */
import { str, fieldValue } from './fields';

export interface ExportColumn {
  col: string;
  get: (lead: any) => string;
}

const lvl1p = (lead: any) => lead.lvl1_person ?? {};
const lvl1c = (lead: any) => lead.lvl1_company ?? {};
const lvl2 = (lead: any) => lead.lvl2 ?? {};
const first = (...vals: unknown[]) => { for (const v of vals) { const s = fieldValue(v); if (s) return s; } return ''; };
const socialUrl = (lead: any, idKeys: string[], urlKey: string) =>
  first(...idKeys.map(k => lead[k] ?? lvl2(lead)[k]), lvl1c(lead)[urlKey]);

export const EXPORT_COLUMNS: ExportColumn[] = [
  { col: 'client', get: l => first(lvl2(l).client, l.client) },
  { col: 'person.id', get: l => first(lvl1p(l).id, l.lead_id) },
  { col: 'person.name', get: l => first(lvl1p(l).name, l.person_name) },
  { col: 'person.status', get: l => first(lvl1p(l).status, l.status) },
  { col: 'person.icp_score', get: l => first(lvl2(l).icp_score, l.icp_score) },
  { col: 'person.overview', get: l => str(lvl2(l).overview) },
  { col: 'person.preferred_language', get: l => first(lvl1p(l).preferred_language, lvl2(l).preferred_language) },
  { col: 'person.country', get: l => first(lvl1p(l).country, l.country) },
  { col: 'person.state', get: l => first(lvl1p(l).state, l.state) },
  { col: 'person.city', get: l => first(lvl1p(l).city, l.city) },
  { col: 'person.full_address', get: l => str(lvl1p(l).full_address) },
  { col: 'person.gender', get: () => '' }, // not present in any payload source
  { col: 'person.role_position', get: l => first(lvl1p(l).role_position, l.person_role) },
  { col: 'person.timezone', get: l => first(lvl1p(l).timezone, lvl2(l).timezone) },
  { col: 'person.email_id', get: l => first(lvl1p(l).email_id, l.person_email) },
  { col: 'person.linkedin_id', get: l => socialUrl(l, ['linkedin_id'], 'linkedin_url') },
  { col: 'person.phone_id', get: l => first(lvl1p(l).phone_id, l.phone_id) },
  { col: 'person.whatsapp_id', get: l => first(lvl1p(l).whatsapp_id) },
  { col: 'person.twitter_x_id', get: l => socialUrl(l, ['twitter_x_id'], 'twitter_url') },
  { col: 'person.instagram_id', get: l => socialUrl(l, ['instagram_id'], 'instagram_url') },
  { col: 'company.id', get: l => first(lvl1c(l).id, l.company_id) },
  { col: 'company.name', get: l => first(lvl1c(l).name, l.company_name) },
  { col: 'company.country', get: l => first(lvl1c(l).country, l.company_country) },
  { col: 'company.timezone', get: l => first(lvl1c(l).timezone) },
  { col: 'company.country_code', get: l => str(lvl1c(l).country_code) },
  { col: 'company.continent', get: l => first(lvl1c(l).continent, l.continent) },
  { col: 'company.state', get: l => first(lvl1c(l).state, l.state) },
  { col: 'company.postal_code', get: l => str(lvl1c(l).postal_code) },
  { col: 'company.city', get: l => first(lvl1c(l).city, l.city) },
  { col: 'company.full_address', get: l => str(lvl1c(l).full_address) },
  { col: 'company.industry', get: l => first(lvl1c(l).industry, l.company_industry) },
  { col: 'company.nr_employees', get: l => first(lvl1c(l).nr_employees, l.nr_employees, l.company_nr_employees) },
  { col: 'company.size_range', get: l => first(lvl1c(l).size_range, l.size_range) },
  { col: 'company.cnae_text', get: l => first(lvl1c(l).cnae_text, l.cnae_text) },
  { col: 'company.preferred_language', get: l => first(lvl1c(l).preferred_language, lvl1c(l).native_language) }, // only when the envelope carries it
  { col: 'company.summary', get: l => str(lvl1c(l).summary) },
  { col: 'company.last_enriched', get: l => first(l.last_enriched) },
  { col: 'company.revenue', get: l => first(lvl1c(l).revenue, l.revenue, l.company_revenue) },
  { col: 'company.email_id', get: l => first(lvl1c(l).email_id, l.company_email) },
  { col: 'company.facebook_id', get: l => socialUrl(l, ['company_facebook'], 'facebook_url') },
  { col: 'company.facebook_score', get: () => '' }, // not present in any payload source
  { col: 'company.linkedin_id', get: l => socialUrl(l, ['company_linkedin'], 'linkedin_url') },
  { col: 'company.mobile_id', get: l => first(lvl1c(l).mobile_id) },
  { col: 'company.phone_id', get: l => first(lvl1c(l).phone_id, l.company_phone) },
  { col: 'company.tiktok_id', get: l => socialUrl(l, ['company_tiktok'], 'tiktok_url') },
  { col: 'company.twitter_x_id', get: l => socialUrl(l, ['company_twitter'], 'twitter_url') },
  { col: 'company.vat_id', get: l => first(lvl1c(l).vat_id, lvl1c(l).cif, lvl1c(l).vat_number, lvl1c(l).tax_id, lvl2(l).company_vat_id) },
  { col: 'company.website_id', get: l => first(lvl1c(l).website_id, lvl1c(l).website, l.company_website) },
  { col: 'company.whatsapp_id', get: l => first(lvl1c(l).whatsapp_id, l.company_whatsapp) },
  { col: 'company.youtube_id', get: l => socialUrl(l, ['company_youtube'], 'youtube_url') },
];

/** The directory statuses the export covers (matches the campaign templates). */
export const EXPORT_STATUSES = ['new_lead','enriching','enriched','outreaching','outreached','engaged'];
